<template>
    <div>
        <nav aria-label="breadcrumb" v-if="id">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <router-link :to="breadcrumbLinkSessions">Sessions ({{ session.difficultyLevel }} - {{
                        session.difficulty }})
                    </router-link>
                </li>
                <li class="breadcrumb-item active">
                    <router-link :to="breadcrumbLinkSession">Session {{ session.dataId || 'TUTORIAL' }} <span
                            v-if="sessionLineDisplay">({{ sessionLineDisplay }})</span></router-link>
                </li>
                <li class="breadcrumb-item active">{{ title }}</li>
            </ol>
        </nav>

        <div class="alert alert-danger" v-if="publishError">
            <strong>Cannot publish this scenario at the moment:</strong> {{ publishError }}
        </div>

        <div class="alert alert-warning" v-if="isDraft">
            <strong>NOTE:</strong> This scenario is currently in draft state. In order to publish, first make sure that
            there are no invalid values, then save the changes, and finally press the "Publish" button.<br/>
            <br/>
            <strong>Last publish time:</strong> {{ draftDetails.updateTime }}<br/>
            <br/>
            <button
                    class="btn btn-danger"
                    v-on:click="revertDraft"
                    :class="!canRevertDraft ? 'disabled' : ''"
                    :disabled="!canRevertDraft"
                    v-text="revertingDraft ? 'Reverting draft changes..' : 'Revert all draft changes'"
            ></button>
        </div>

        <div class="scenario-update-header row mb-3">
            <div class="col-sm-6 text-left">
                <h1>
                    <editable-text-field
                            :value="title"
                            :defaultValue="defaultTitle"
                            placeholder="Scenario name"
                            v-on:input="onNameUpdated"
                    />
                </h1>
            </div>
            <div class="col-sm-6 text-right">
                <button
                        v-if="canClearUnsavedChanges"
                        class="btn btn-danger"
                        v-on:click="clearUnsavedChanges"
                        :class="!canClearUnsavedChanges ? 'disabled' : ''"
                        :disabled="!canClearUnsavedChanges"
                        v-text="'Clear unsaved changes'"
                />

                <button
                        v-else
                        class="btn btn-danger"
                        v-on:click="clearScenario"
                        :class="!canClearScenario ? 'disabled' : ''"
                        :disabled="!canClearScenario"
                        v-text="clearingScenario ? 'Clearing scenario..' : 'Clear scenario'"
                />

                <button
                        class="btn btn-success ml-3"
                        v-on:click="save"
                        :class="!canSave || saving ? 'disabled' : ''"
                        :disabled="!canSave || saving"
                        v-text="saving ? 'Saving draft..' : 'Save draft'"
                />

                <button
                        class="btn btn-info ml-3"
                        v-on:click="publish"
                        :class="!canPublish || publishing ? 'disabled' : ''"
                        :disabled="!canPublish || publishing"
                        v-text="publishing ? 'Publishing..' : 'Publish'"
                        :title="hasChanges ? 'You cannot publish if there are unsaved changes' : ''"
                />

                <button
                        class="btn btn-info ml-3"
                        v-on:click="play"
                        :class="!canPlay ? 'disabled' : ''"
                        :disabled="!canPlay"
                >Play</button>
            </div>
        </div>

        <div v-if="loading">
            Loading scenario..
        </div>
        <div v-else>
            <div class="card" :class="crimeOk ? 'card-ok' : 'card-error'">
                <div class="card-header"><strong>Crime</strong></div>
                <div class="card-body">
                    <div class="form-row">
                        <div class="form-group col-md-6">
                            <label>Crime<span v-if="isCrimeRequired">*</span></label>
                            <select v-model="crime" class="form-control" :disabled="!canSetCrime">
                                <option value="">Select crime</option>
                                <option value="trafficking">Trafficking</option>
                                <option value="smuggling">Smuggling</option>
                                <option value="abduction">Abduction</option>
                                <option value="drugTrafficking">Drug Trafficking</option>
                                <option value="terrorism">Terrorism</option>
                            </select>
                        </div>
                        <div class="form-group col-md-6">
                            <label>Sub crime<span v-if="isSubCrimeRequired">*</span></label>
                            <select v-model="subCrime" class="form-control" :disabled="!canSetSubCrime">
                                <option value=""
                                        v-text="crime && crime !== 'trafficking' ? 'Only trafficking requires a sub crime' : 'Select sub crime (L2 / L3)'"></option>
                                <option value="sexual">Sexual</option>
                                <option value="criminal">Criminal</option>
                                <option value="begging">Begging</option>
                                <option value="forcedLabour">Forced Labour</option>
                                <option value="marriage">Marriage</option>
                                <option value="organs">Organs</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header"><strong>Info</strong></div>
                <div class="card-body">
                    <div class="form-row">
                        <div class="form-group col-md-6">
                            <label>Author</label>
                            <input class="form-control" v-model="author" placeholder="Author"/>
                        </div>
                        <div class="form-group col-md-6">
                            <label>Notes</label>
                            <textarea class="form-control" v-model="notes" placeholder="Notes"></textarea>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header"><strong>People</strong></div>
                <div class="card-body">
                    <people-selector v-model="people" :session="this.session"/>
                </div>
            </div>
            <div class="card mt-3">
                <div class="card-header"><strong>Indicators</strong></div>
                <div class="card-body">
                    <div class="alert alert-dark" v-if="loadingIndicators">
                        Loading indicators..
                    </div>
                    <indicator-selector v-model="indicators" v-if="!loadingIndicators" :allIndicators="indicators"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { $eBus } from '@/utils/eventBus';

    import PeopleSelector from '@/components/admin/people-selector';
    import IndicatorSelector from '@/components/admin/indicator-selector';
    import * as MutationTypes from '@/store/mutation-types';
    import Vue from 'vue';
    import {validateScenario} from "@/utils/admin/indicators/ExceptionValidator";
    import fastDeepEqual from "fast-deep-equal";
    import cloneDeep from "clone-deep";
    import EditableTextField from "@/components/common/editable-text-field/index";
    import {getGameUrl} from "../../../../utils/urlManager";
    import { mapGetters } from 'vuex';

    export default {
        name: 'scenarios-update',
        components: {
            EditableTextField,
            PeopleSelector,
            IndicatorSelector
        },
        data() {
            return {
                loading: true,
                loadingIndicators: true,

                canSave: false,
                saving: false,

                updateTime: null,
                revertingDraft: false,
                hasChanges: false,
                draftDetails: null,

                publishError: null,
                published: false,
                publishing: false,
                canPublish: false,
                clearingScenario: false,

                session: null,

                id: null,
                index: 0,
                name: null,
                crime: '',
                subCrime: '',
                author: null,
                notes: null,
                indicators: [],
                people: [],
                hasCrime: null,

                initialData: null,

                allObservations: {}
            }
        },
        mounted() {
            this.load();
        },
        methods: {
            publish() {
                if (!this.canPublish) {
                    return;
                }

                if (this.published) {
                    return;
                }

                if (this.publishing) {
                    return;
                }

                this.publishing = true;

                this.$apiClient.publishScenario(this.id).then(() => {
                    this.$notify({
                        message: `Scenario ${this.id} successfully published`,
                        type: 'success'
                    });

                    Vue.set(this, 'draftDetails', null);

                    this.publishing = false;
                    this.published = true;

                    this.updateCanPublish();
                    this.updateCanSave();
                }, (error) => {
                    this.publishing = false;

                    this.$notify({
                        message: `Could not publish indicator: ${error}`,
                        type: 'danger'
                    });
                })
            },
            save() {
                if (!this.canSave) {
                    return;
                }

                if (this.saving) {
                    return;
                }

                let jsonData = JSON.stringify(this.data, null, ' ');

                this.saving = true;

                this.$apiClient.updateScenario(this.scenarioId, jsonData).then((response) => {
                    this.saving = false;

                    this.hasChanges = false;
                    this.canSave = false;
                    this.published = false;

                    this.draftDetails = response.draftDetails;

                    this.initialData = cloneDeep(this.data);

                    this.$notify({
                        message: `Scenario ${this.scenarioId} successfully saved`,
                        type: 'success'
                    });

                    this.updateCanPublish();
                    this.updateCanSave();
                }).catch((error) => {
                    this.$notify({
                        message: `Could not save indicator: ${error}`,
                        type: 'danger'
                    });
                });

                console.log('savingJsonData', jsonData)
            },
            load() {
                this.canSave = false;
                this.loading = true;
                this.loadingIndicators = true;

                this.$apiClient.getScenario(this.scenarioId).then((scenario) => {
                    this.loading = false;

                    this.$store.commit(MutationTypes.SET_ACTIVE_SCENARIO, scenario);

                    this.session = scenario.session;
                    this.name = scenario.name || null;
                    this.id = scenario.id;
                    this.hasCrime = scenario.hasCrime;
                    this.crime = scenario.crime && scenario.crime.crime || '';
                    this.subCrime = scenario.crime && scenario.crime.subCrime || '';
                    this.author = scenario.author || null;
                    this.notes = scenario.notes || null;
                    this.indicators = scenario.indicators || [];
                    this.people = scenario.people || [];
                    this.index = scenario.index || 0;

                    if (!this.crime && typeof scenario.crime === 'string') {
                        this.crime = scenario.crime;
                    }

                    if (!this.subCrime && typeof scenario.subCrime === 'string') {
                        this.subCrime = scenario.subCrime;
                    }

                    this.hasChanges = false;
                    this.published = !scenario.draftDetails;
                    this.draftDetails = scenario.draftDetails;

                    this.initialData = cloneDeep(this.data);

                    this.$store.commit(MutationTypes.SET_ACTIVE_SCENARIO_DATA, this.data);

                    this.$apiClient.getIndicatorsByScenario(this.scenarioId).then((indicators) => {
                        this.loadingIndicators = false;

                        this.$store.commit(MutationTypes.SET_SCENARIO_INDICATORS, indicators);

                    });
                }).catch((error) => {
                    alert(error);
                });
            },
            clearUnsavedChanges() {
                this.load();
            },
            clearScenario() {
                if (!this.canClearScenario) {
                    return false;
                }

                let confirmResult = confirm('Are you sure that you want to clear the scenario? The ENTIRE scenario will be cleared and NOTHING will be kept. This cannot be undone.');

                if (!confirmResult) {
                    return false;
                }

                this.clearingScenario = true;

                this.$apiClient.clearScenario(this.scenarioId).then(() => {
                    this.clearingScenario = false;

                    this.$notify({
                        message: `Scenario ${this.scenarioId} successfully cleared`,
                        type: 'success'
                    });

                    this.$nextTick(() => {
                        this.load();
                    });
                }, (error) => {
                    this.clearingScenario = false;

                    this.$notify({
                        message: `Could not clear scenario: ${error}`,
                        type: 'danger'
                    });
                });
            },
            revertDraft() {
                if (!this.canRevertDraft) {
                    return false;
                }

                let confirmResult = confirm('Are you sure you want to revert all draft changes? This will restore the scenario to the last time the scenario was published.');

                if (!confirmResult) {
                    return false;
                }

                this.revertingDraft = true;

                this.$apiClient.revertScenarioDraftChanges(this.scenarioId).then(() => {
                    this.revertingDraft = false;

                    this.$notify({
                        message: `Scenario ${this.scenarioId} successfully reverted`,
                        type: 'success'
                    });

                    this.$nextTick(() => {
                        Vue.set(this, 'draftDetails', null);

                        this.publishing = false;

                        this.updateCanPublish();
                        this.updateCanSave();

                        this.published = true;

                        this.load();
                    });
                }, (error) => {
                    this.revertingDraft = false;

                    this.$notify({
                        message: `Could not revert scenario: ${error}`,
                        type: 'danger'
                    });
                });
            },
            normalizeData(data) {
                let indicatorCount = data.indicators ? data.indicators.length : 0;

                if (indicatorCount) {
                    for (let i = 0; i < indicatorCount; i++) {
                        if (!data.indicators[i].dependencies) {
                            data.indicators[i].dependencies = [];
                        }

                        delete data.indicators[i].index;
                    }
                }

                return data;
            },
            onNameUpdated(newName) {
                this.name = newName.replace(/^\s+|\s+$/, '') || null;
            },
            updateCanPublish() {
                this.publishError = null;

                if (this.published) {
                    //this.publishError = 'Already published';
                    this.canPublish = false;
                } else if (this.hasChanges) {
                    //this.publishError = 'Cannot publish without first saving to a draft';
                    this.canPublish = false;
                } else {
                    try {
                        validateScenario(this.data, this.session);
                    } catch (error) {
                        this.publishError = error.message || error.toString();
                    }

                    this.canPublish = !this.publishError;
                }
            },
            updateCanSave() {
                this.canSave = this.hasChanges;
            },
            play() {
                if (!this.canPlay) {
                    return;
                }

                const url = getGameUrl(`/game/play/${this.id}`);

                window.location.href = url;
            }
        },
        computed: {
            crimeOk() {
                if (this.isCrimeRequired && !this.crime) {
                    return false;
                }

                if (this.isSubCrimeRequired && !this.subCrime) {
                    return false;
                }

                return true;
            },
            canPlay() {
                return !this.hasChanges && this.published;
            },
            title() {
                return this.name || this.defaultTitle;
            },
            defaultTitle() {
                let indexPlusOne = this.index + 1;

                return `Scenario ${indexPlusOne}`;
            },
            canRevertDraft() {
                if (this.clearingScenario) {
                    return false;
                }

                if (this.canClearUnsavedChanges) {
                    return false;
                }

                if (!this.isDraft) {
                    return false;
                }

                if (this.revertingDraft) {
                    return false;
                }

                return true;
            },
            canClearScenario() {
                return !this.clearingScenario && !this.hasChanges;
            },
            canClearUnsavedChanges() {
                return !this.clearingScenario && this.hasChanges;
            },
            isDraft() {
                return !!this.draftDetails;
            },
            sessionLineDisplay() {
                if (!this.session) {
                    return;
                }

                if (!this.session.line) {
                    return;
                }

                if (this.session.line === 'first') {
                    return 'FLC';
                } else if (this.session.line === 'second') {
                    return 'SLC';
                }

                return;
            },
            canSetCrime() {
                return this.isCrimeRequired;
            },
            canSetSubCrime() {
                if (!this.session) {
                    return false;
                }

                if (!this.canSetCrime) {
                    return false; // If you cannot set a crime, you also can't set a sub crime
                }

                return this.crime == 'trafficking';
            },
            isCrimeRequired() {
                if (!this.session) {
                    return;
                }

                return this.hasCrime > 0;
            },
            isSubCrimeRequired() {
                if (!this.session) {
                    return;
                }

                if (!this.canSetCrime) {
                    return false; // If you cannot set a crime, you also can't set a sub crime
                }

                if (this.crime === 'trafficking') {
                    return this.session.difficultyLevel === 'L2' || this.session.difficultyLevel === 'L3';
                } else {
                    return 0;
                }
            },
            breadcrumbLinkSessions() {
                if (!this.session) {
                    return;
                }

                console.log('session is ', this.session);

                return {
                    path: '/admin/sessions',
                    query: {
                        difficultyLevel: this.session.difficultyLevel,
                        difficulty: this.session.difficulty,
                    }
                }
            },
            breadcrumbLinkSession() {
                if (!this.session) {
                    return;
                }

                return `/admin/sessions/update/${this.session.id}`
            },
            data() {
                let data = this.normalizeData({
                    id: this.id,
                    author: this.author,
                    notes: this.notes,
                    crime: this.crime,
                    subCrime: this.subCrime,
                    indicators: this.indicators,
                    people: this.people,
                    name: this.name,
                });

                //console.log(data);

                return data;
            },
            scenarioId() {
                return this.$route.params.id;
            },
        },
        watch: {
            scenarioId() {
                this.load();
            },
            data: {
                // This will let Vue know to look inside the array
                deep: true,

                // We have to move our method to a handler field
                handler(newData) {
                    if (this.initialData) {
                        this.hasChanges = !fastDeepEqual(this.initialData, newData);
                    }

                    this.$store.commit(MutationTypes.SET_ACTIVE_SCENARIO_DATA, this.data);

                    this.updateCanPublish();
                    this.updateCanSave();
                }
            },
            crime(currentValue) {
                if (currentValue !== 'trafficking') {
                    this.subCrime = '';
                }
            }
        }
    }
</script>

<style scoped lang="scss">
    .crime {
        &.crime-no-crime {
            background: #ffbbbb;
            color: #bb0000;
        }

        &.crime-no-crime-selected {

        }

        &.crime-has-crime {

        }
    }

    .scenario-update-header {
        position: relative;

        > .btn-save {
            position: absolute;
            right: 0;
            top: 0;
        }
    }

    nav {
        position: relative;

        .session-line {
            position: absolute;
            top: 15px;
            right: 15px;
        }
    }

    .card {
        &.card-ok {

        }

        &.card-error {
            background: #ffbcbcbc;
        }
    }
</style>
